import { useContext } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import FetchContext from '../../context/fetch/FetchContext'

const TITLE = 'הסרה מרשימת התפוצה'
const PROMPT = 'להסיר את הכתובת הזו מרשימת התפוצה של הודעות פרסומיות?'
const CONFIRM_LABEL = 'הסירו אותי'
const DECLINE_LABEL = 'אל תסירו אותי'
const ALREADY_REMOVED = 'הכתובת הזו כבר הוסרה מרשימת התפוצה.'
const LOADING = 'רגע…'

// The same words whichever answer was given, so that a recipient who confirms and one who
// declines cannot tell from the page which addresses the system holds.
const ACKNOWLEDGEMENT = 'תודה. בקשתך התקבלה.'

// Reached by a recipient with no account, from a link in an announcement email, so it
// sits outside the authenticated layout entirely. Retrieving it must change nothing:
// mail scanners open every link in a message before a person reads it, which is why the
// read below is a read and the opt-out waits for a button.
export default function UnsubscribePage() {
    const { token } = useParams()
    const fetcher = useContext(FetchContext)

    const { data, isPending } = useQuery({
        queryKey: ['unsubscribe', token],
        queryFn: () => fetcher.fetchUnsubscribe(token),
        retry: false,
        refetchOnWindowFocus: false,
    })

    const answer = useMutation({
        mutationFn: (confirmed) => fetcher.fetch({
            method: 'post',
            path: `/public/unsubscribe/${token}/confirm`,
            data: { confirmed },
            authorize: false,
        }),
    })

    return (
        <MDBox
            component="main"
            dir="rtl"
            display="flex"
            flexDirection="column"
            alignItems="center"
            textAlign="center"
            gap="1rem"
            p={4}
        >
            <MDTypography variant="h4" component="h1">{TITLE}</MDTypography>

            {renderBody()}
        </MDBox>
    )

    function renderBody() {
        if (answer.isSuccess) {
            return (
                <MDTypography variant="body2">
                    {answer.data?.message ?? ACKNOWLEDGEMENT}
                </MDTypography>
            )
        }

        if (isPending) {
            return <MDTypography variant="body2">{LOADING}</MDTypography>
        }

        if (data?.alreadyUnsubscribed) {
            return <MDTypography variant="body2">{ALREADY_REMOVED}</MDTypography>
        }

        return (
            <>
                <MDTypography variant="body2">{PROMPT}</MDTypography>

                {data?.address != null && (
                    <MDTypography variant="body2" fontWeight="medium">
                        {data.address}
                    </MDTypography>
                )}

                <MDBox display="flex" justifyContent="center" gap="1rem">
                    <MDButton
                        data-testid="unsubscribe-confirm"
                        color="primary"
                        variant="contained"
                        disabled={answer.isPending}
                        onClick={() => answer.mutate(true)}
                    >
                        {CONFIRM_LABEL}
                    </MDButton>

                    <MDButton
                        data-testid="unsubscribe-decline"
                        color="secondary"
                        variant="outlined"
                        disabled={answer.isPending}
                        onClick={() => answer.mutate(false)}
                    >
                        {DECLINE_LABEL}
                    </MDButton>
                </MDBox>
            </>
        )
    }
}
